import { useCallback, useEffect, useState } from 'react'
import { BulkCopyFilesArgs, FolderClient } from 'api/clients/FolderClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { ArticleFlat, FolderBatchCopyStatus } from 'types'

const FOLDER_BATCH_COPY_CHANNEL = 'FolderBatchCopyChannel'

type FolderBatchCopyChannelPayload = {
  type: 'status_changed'
  status: FolderBatchCopyStatus
  failure_reason: string | null
  files?: ArticleFlat[]
}

export type BulkCopyResult =
  | { type: typeof SUCCESS_RESPONSE_TYPE; data: { files: ArticleFlat[] } }
  | {
      type: 'error'
      data: string
      status: number
      statusText: string
      url: string
      headers: Record<string, string>
    }

const makeError = (reason: string): BulkCopyResult => ({
  type: 'error',
  data: reason,
  status: 0,
  statusText: '',
  url: '',
  headers: {},
})

export const useBulkCopyFiles = () => {
  const websocketConnection = useWebsocketConnection()
  const [jobId, setJobId] = useState<number | null>(null)
  const [destinationWorkspaceId, setDestinationWorkspaceId] = useState<number | null>(null)
  const [result, setResult] = useState<BulkCopyResult | null>(null)

  useEffect(() => {
    if (!websocketConnection || jobId === null || destinationWorkspaceId === null) return

    const subscription = websocketConnection.subscriptions.create(
      { channel: FOLDER_BATCH_COPY_CHANNEL, workspace_id: destinationWorkspaceId, id: jobId },
      {
        received(payload: FolderBatchCopyChannelPayload) {
          if (payload?.type !== 'status_changed') return
          if (payload.status === FolderBatchCopyStatus.Successful) {
            setResult({ type: SUCCESS_RESPONSE_TYPE, data: { files: payload.files ?? [] } })
          } else if (
            payload.status === FolderBatchCopyStatus.Failed ||
            payload.status === FolderBatchCopyStatus.Aborted ||
            payload.status === FolderBatchCopyStatus.Expired
          ) {
            setResult(makeError(payload.failure_reason ?? 'Copy failed'))
          }
        },
      },
    )

    return () => subscription.unsubscribe()
  }, [websocketConnection, jobId, destinationWorkspaceId])

  const startCopy = useCallback(async (args: BulkCopyFilesArgs) => {
    setResult(null)
    const kickoff = await FolderClient.bulkCopyFiles(args)
    if (kickoff.type !== SUCCESS_RESPONSE_TYPE) {
      setResult(makeError(typeof kickoff.data === 'string' ? kickoff.data : 'Failed to start copy'))
      return
    }
    setJobId(kickoff.data.id)
    setDestinationWorkspaceId(args.destinationWorkspaceId)
  }, [])

  return { startCopy, result }
}
